/**
 * Client-side PDF form filler that captures exactly what the preview shows.
 *
 * Strategy:
 * 1. Use pdfjs-dist to render each template page to an offscreen canvas (same as preview)
 * 2. Draw field values / signatures directly onto that canvas
 * 3. Create a new PDF from the canvas images using pdf-lib
 *
 * This bypasses pdf-lib's inability to parse certain complex PDFs while producing
 * a filled PDF that matches the on-screen preview pixel-for-pixel.
 */

import { PDFDocument } from "pdf-lib";
import {
  drawCenteredTextInBox,
  fontSizeForCanvasFieldBox,
  resolveSplitParts,
  sliceValue,
  splitPartToPixels,
} from "@/lib/splitFieldCoords";

export interface FormFieldForPdf {
  id: number;
  fieldName: string;
  fieldLabel: string;
  fieldType: string;
  pageNumber: number | null;
  fieldCoordinates: any;
}

export interface FilledFormData {
  /** The template PDF document's ID (used to fetch the raw PDF) */
  documentId?: number;
  /** Direct URL to fetch the PDF content (used for carrier forms) */
  pdfContentUrl?: string;
  /** Form fields with placement info */
  fields: FormFieldForPdf[];
  /** Map of field.id → populated value string (or data:image URL for signatures) */
  fieldValues: Record<number, string>;
}

/** Load pdfjs-dist dynamically (same pattern as PdfFieldPlacer) */
async function loadPdfjs() {
  let pdfjsLib: any;
  try {
    pdfjsLib = await import("pdfjs-dist");
  } catch {
    pdfjsLib = await import("pdfjs-dist/legacy/build/pdf.mjs");
  }

  let workerUrl: string;
  try {
    workerUrl = (await import("pdfjs-dist/build/pdf.worker.mjs?url")).default;
  } catch {
    workerUrl = (await import("pdfjs-dist/legacy/build/pdf.worker.mjs?url")).default;
  }
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
  return pdfjsLib;
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/**
 * Fetch the template PDF, render pages via pdfjs-dist, draw field overlays
 * on the canvas, then assemble into a new PDF.
 */
export async function generateFilledPdf(
  data: FilledFormData
): Promise<Uint8Array> {
  const pdfjsLib = await loadPdfjs();

  // 1. Fetch & load using pdfjs-dist (handles complex PDFs)
  const pdfUrl = data.pdfContentUrl || `/api/documents/${data.documentId}/content?disposition=inline`;
  const loadingTask = pdfjsLib.getDocument({ url: pdfUrl, withCredentials: true });
  const pdfDoc = await loadingTask.promise;
  const numPages = pdfDoc.numPages;

  // Use a high scale for crisp output
  const RENDER_SCALE = 2;

  // 2. Create a new blank PDF with pdf-lib
  const outPdf = await PDFDocument.create();

  // 3. For each page: render to canvas, draw overlays, add to output
  for (let pageNum = 1; pageNum <= numPages; pageNum++) {
    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: RENDER_SCALE });

    // Create offscreen canvas
    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) continue;

    // Render PDF page
    await page.render({ canvasContext: ctx, viewport }).promise;

    // Draw field overlays for this page
    const pageFields = data.fields.filter(
      (f) => f.pageNumber === pageNum && f.fieldCoordinates?.unit === "relative"
    );

    for (const field of pageFields) {
      const value = data.fieldValues[field.id];
      if (!value) continue;

      const coords = field.fieldCoordinates;
      const x = clamp01(coords.x ?? 0) * canvas.width;
      const y = clamp01(coords.y ?? 0) * canvas.height;
      const w = clamp01(coords.width ?? 0.1) * canvas.width;
      const h = clamp01(coords.height ?? 0.02) * canvas.height;

      // Handle signature / image values
      if (value.startsWith("data:image")) {
        try {
          const img = await loadImage(value);
          // Preserve aspect ratio and center
          const imgAspect = img.width / img.height;
          const boxAspect = w / h;
          let drawW = w;
          let drawH = h;
          let drawX = x;
          let drawY = y;

          if (imgAspect > boxAspect) {
            drawH = w / imgAspect;
            drawY = y + (h - drawH) / 2;
          } else {
            drawW = h * imgAspect;
            drawX = x + (w - drawW) / 2;
          }
          ctx.drawImage(img, drawX, drawY, drawW, drawH);
        } catch (err) {
          console.warn(`Could not draw signature image for "${field.fieldName}":`, err);
        }
        continue;
      }

      // Split fields: splitParts boxes or legacy segmentation-derived layout
      const splitParts = resolveSplitParts(coords);
      if (splitParts) {
        let drewSplit = false;
        for (const part of splitParts) {
          const sliceText = sliceValue(value, part.start, part.length);
          if (!sliceText) continue;
          const box = splitPartToPixels(part, canvas.width, canvas.height);
          drawCenteredTextInBox(ctx, sliceText, box, { referenceHeightPx: h });
          drewSplit = true;
        }
        if (drewSplit) continue;
      }

      // Handle segmented fields (SSN-like character boxes)
      const seg = coords.segmentation;
      if (seg?.enabled) {
        const segCount = seg.count ??
          (seg.boundaries ? seg.boundaries.length + 1 : Math.max(1, Math.floor(w / h)));
        const boundaries: number[] = seg.boundaries ??
          Array.from({ length: segCount - 1 }, (_: unknown, i: number) => (i + 1) / segCount);
        const stops = [0, ...boundaries, 1];
        const chars = value.replace(/[^a-zA-Z0-9]/g, "").split("");

        ctx.textBaseline = "middle";

        for (let i = 0; i < Math.min(stops.length - 1, chars.length); i++) {
          const segStart = stops[i];
          const segEnd = stops[i + 1];
          const segW = (segEnd - segStart) * w;
          const segX = x + segStart * w;
          const charFontSize = Math.min(h * 0.7, segW * 0.8);
          ctx.font = `${charFontSize}px Helvetica, Arial, sans-serif`;
          const metrics = ctx.measureText(chars[i]);
          const charX = segX + (segW - metrics.width) / 2;
          const charY = y + h / 2;

          // Draw segment divider
          if (i > 0) {
            ctx.strokeStyle = "rgba(0,0,0,0.3)";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(segX, y);
            ctx.lineTo(segX, y + h);
            ctx.stroke();
          }

          ctx.fillStyle = "black";
          ctx.fillText(chars[i], charX, charY);
        }
        continue;
      }

      // Handle checkbox
      if (field.fieldType === "checkbox") {
        if (value && value !== "false" && value !== "0") {
          const checkSize = Math.min(h, w) * 0.7;
          ctx.font = `${checkSize}px Helvetica, Arial, sans-serif`;
          ctx.fillStyle = "black";
          ctx.textBaseline = "middle";
          const checkX = x + (w - ctx.measureText("✓").width) / 2;
          ctx.fillText("✓", checkX, y + h / 2);
        }
        continue;
      }

      // Regular text
      const fontSize = fontSizeForCanvasFieldBox(w, h, value, ctx);
      ctx.font = `${fontSize}px Helvetica, Arial, sans-serif`;
      ctx.fillStyle = "black";
      ctx.textBaseline = "middle";
      const textX = x + 2;
      const textY = y + h / 2;

      // Clip text to field width
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.clip();
      ctx.fillText(value, textX, textY);
      ctx.restore();
    }

    // Convert canvas to PNG and embed in output PDF
    const pngDataUrl = canvas.toDataURL("image/png");
    const pngBase64 = pngDataUrl.replace(/^data:image\/png;base64,/, "");
    const pngBytes = Uint8Array.from(atob(pngBase64), (c) => c.charCodeAt(0));

    const image = await outPdf.embedPng(pngBytes);

    // Set page size to match original PDF page dimensions (in points, not scaled)
    const originalViewport = page.getViewport({ scale: 1 });
    const outPage = outPdf.addPage([originalViewport.width, originalViewport.height]);
    outPage.drawImage(image, {
      x: 0,
      y: 0,
      width: originalViewport.width,
      height: originalViewport.height,
    });
  }

  // Clean up
  pdfDoc.destroy();

  return outPdf.save();
}

/** Load an image from a data URL and return an HTMLImageElement */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

